import { useMemo } from 'react';
import type { TrackerChannel } from '../types/tracker.types';
import {
  StatusGroupRule,
  RuleEvaluation,
  evaluateRulesForChannels,
  PREDEFINED_RULES,
} from '../utils/statusGroupRules';

/**
 * Hook to evaluate status group rules against tracker channels
 *
 * @param channels - Array of tracker channels to evaluate
 * @param customRules - Optional custom rules to evaluate (in addition to predefined rules)
 * @returns Map of channel IDs to their triggered rule evaluations
 */
export function useStatusGroupRules(
  channels: TrackerChannel[],
  customRules: StatusGroupRule[] = []
): {
  ruleEvaluations: Map<string, RuleEvaluation[]>;
  channelsWithRules: TrackerChannel[];
  criticalCount: number;
  warningCount: number;
  infoCount: number;
} {
  // Combine predefined and custom rules
  const allRules = useMemo(() => {
    return [...PREDEFINED_RULES, ...customRules];
  }, [customRules]);

  // Evaluate rules
  const ruleEvaluations = useMemo(() => {
    return evaluateRulesForChannels(allRules, channels);
  }, [allRules, channels]);

  // Get channels that have triggered rules
  const channelsWithRules = useMemo(() => {
    return channels.filter(channel => ruleEvaluations.has(channel.id));
  }, [channels, ruleEvaluations]);

  // Count by severity
  const { criticalCount, warningCount, infoCount } = useMemo(() => {
    let critical = 0;
    let warning = 0;
    let info = 0;

    for (const evaluations of ruleEvaluations.values()) {
      for (const evaluation of evaluations) {
        switch (evaluation.severity) {
          case 'critical':
            critical++;
            break;
          case 'warning':
            warning++;
            break;
          case 'info':
            info++;
            break;
        }
      }
    }

    return { criticalCount: critical, warningCount: warning, infoCount: info };
  }, [ruleEvaluations]);

  return {
    ruleEvaluations,
    channelsWithRules,
    criticalCount,
    warningCount,
    infoCount,
  };
}

export default useStatusGroupRules;
